/**
 * Funding source budget tab when there are not wallets
 * - project/wallet is off
 * - budget is on
 * 
 * Who sees it:
 * - Reseller
 * - Customer
 * 
 * Page to allow a reseller or customer to manage their budget settings.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import { Button, Grid, Paper, Switch, Tooltip, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import withStyles from '@mui/styles/withStyles';
import HelpOutline from '@mui/icons-material/HelpOutline';

import AbEnvironment from '../auth/AbEnvironment'; 

import ConfirmationDialog from '../ui/ConfirmationDialog';
import CurrencyTextField from '../ui/CurrencyTextField';
import LoadingSpinner from '../ui/LoadingSpinner';
import PaymentSourceDialog from '../ui/PaymentSourceDialog';
import SaveButton from '../ui/SaveButton';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import TopUps from '../account/TopUps';

import AddFundsForm from './AddFundsForm';
import FundingSourceBudgetAccountUpdateMutation from './FundingSourceBudgetAccountUpdateMutation';

import BalanceAndFloatWidgets from '../home/BalanceAndFloatWidgets';
import SpendingWidgets from '../home/SpendingWidgets';

import PoweredBy from '../landing/PoweredBy';
import AppBindStyles from '../../AppBindStyles';
import { disableAttributeCheck, getValueForRender } from '../../utils/InputUtils';
import Payouts from './Payouts';
import AutomaticPayoutFieldsAccountUpdateMutation from './AutomaticPayoutFieldsAccountUpdateMutation';
import RefundAndTerminateDialog from '../customer/RefundAndTerminateDialog';

const abEnvironment = new AbEnvironment();

const FundingSourceBudgetQuery = graphql`
query FundingSourceBudgetQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountTByParentId {
     accountId
     accountName
     logo
    }
    accountId
    accountType
    accountStatus
    address1
    floatLimit
    autoTopupActive
    autoTopupAmount
    autoTopupThreshhold
    autoPayout
    autoPayoutRetain
    rAutoTopupAmount
    rAutoTopupThreshold
    rMaxCharge
    rMaxMonthlyBudget
    requestedMaxCharge
    requestedFloatLimit
    maxCharge
    payFromBalance
    maxMonthlyBudget
    useBudgets
    initialBalanceAmount
    paymentSourceTsByAccountId {
     nodes {
       psId
       psType
       psStatus
       psOrder
       ccBrand
       ccLast4
       ccExpy
       fiName
       achAccountNum
       firstName
       lastName
       paypalEmail
       declineCount
       lastDeclineReason
       lastDeclineChargeAmount
       extSource
       extPsId
     }
   }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  sectionBlock: {
    marginBottom: 12,  
  },
  wrapper: {
    marginTop: 16,
    marginBottom: 40
  },
  paper: {
    textAlign: 'left',
    //color: theme.palette.text.secondary,
    maxWidth: 1122,
    marginTop: 16,
    marginBottom: 16,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  currencyField: {
    width: 144,
  },
  currencyField2: {
    width: 176,
  },
  currencyField3: {
    width: 88,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
  },
  noBudgetHolder: {
    width: 88,
    marginTop: 8,
    marginBottom: 8,
    marginRight: 8,
    textAlign: 'right',
  },
  dbTile: {
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
    height: 56,
    width: 264
  },
  dbTileSelected: {
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
    height: 56,
    width: 264,
    backgroundColor: '#D3D3D3'
  },
  dbTileNonSelectable: {
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
    height: 68,
    width: 312
  },
  success: {
    backgroundColor: green[300],
  },
  error: {
    backgroundColor: theme.palette.error.light,
  },

});

class FundingSourceBudget extends Component {
  constructor(props)
  {
    super(props);
    this.state = {
      forceFetch:               0,
      savedAutoTopupActive:     null,
      savedAutoTopupAmount:     null,
      savedAutoTopupThreshold:  null,
      savedMaxMonthlyBudget:    null, // For save button behavior post save
      savedMaxMonthlyBudgetSet: false,

      autoTopupActive: null,
      autoTopupAmount: null,
      autoTopupThreshold: null,

      autoPayout: null,
      autoPayoutRetain: null,
      
      savedAutoPayout: null,
      savedAutoPayoutRetain: null,

      maxMonthlyBudget: null,
      maxMonthlyBudgetSet: false,

      confirmAutoTopup: false,

      // Save statuses for budget
      saveStatusBudget: null,
      saveMessageBudget: null,
      saveSnackbarOpenBudget: false,
      saveSnackbarDurationBudget: null,

      // Save statuses for spend controls
      saveStatusLimit: null,
      saveMessageLimit: null,
      saveSnackbarOpenLimit: false,
      saveSnackbarDurationLimit: null,

      // Save statuses for auto-payout configuration
      saveStatusAutoPayout: null,
      saveMessageAutoPayout: null,
      saveSnackbarOpenAutoPayout: false,
      saveSnackbarDurationAutoPayout: null,

      addFundsNowDialog: false,
      requestCancelDialog: false,
      requestCancelSpinner: false,

      // Save status for add funding
      saveStatusAddNow: null,
      saveMessageAddNow: null,
      saveSnackbarOpenAddNow: false,
      saveSnackbarDurationAddNow: null
    };

  }

  isAutoTopupAmountValid(theAccount) {
    const { getTopupTargetLimit, getTopupTargetDefault, getTopupTargetMinimum, getTopupThresholdDefault} = this.props.auth;

    let val = Number(getValueForRender(this.state.autoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault())); 

    if (val < getTopupTargetMinimum() || val > getTopupTargetLimit()) {
      return false;
    }

    // Don't allow the amout to be less than the threshold
    let autoTopupThreshold = Number(getValueForRender(this.state.autoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault())); 
    if (val < autoTopupThreshold) {
      return false;
    }

    return true;
  }

  getAutoTopupAmountErrorMessage(theAccount) {
    const { getTopupTargetDefault, getTopupThresholdDefault } = this.props.auth;

    let errMesg = null;
    let amountVal    = Number(getValueForRender(this.state.autoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault())); 
    let thresholdVal = Number(getValueForRender(this.state.autoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault())); 

    if (amountVal < thresholdVal) {
      errMesg = 'Cannot be less than "falls below" value';
    }
    return errMesg;
  }

  isAutoTopupAmountError(theAccount) {
    if (this.getAutoTopupAmountErrorMessage(theAccount) == null) {
      return false;
    }
    return true;
  }

  // Determine if the button in the budget paper should be disabled
  isSaveBudgetDisabled(theAccount) {
    const { getTopupThresholdMinimum } = this.props.auth;

    // The budget paper has:
    // - use my balance to pay
    // - auto topup on/off
    // - replenish amount
    // - threshold

    // Get the saved values to compare against.
    let theSavedAutoTopupActive = theAccount.autoTopupActive;
    if (this.state.savedAutoTopupActive != null) {
      theSavedAutoTopupActive = this.state.savedAutoTopupActive;
    }

    let theSavedAutoTopupAmount = theAccount.autoTopupAmount;
    if (this.state.savedAutoTopupAmount != null) {
      theSavedAutoTopupAmount = this.state.savedAutoTopupAmount;
    }

    let theSavedAutoTopupThreshold = theAccount.autoTopupThreshhold;
    if (this.state.savedAutoTopupThreshold != null) {
      theSavedAutoTopupThreshold = this.state.savedAutoTopupThreshold;
    }

    // If nothing has changed, leave it disabled
    let autoTopupActiveChanged = true;
    if (this.state.autoTopupActive == null || this.state.autoTopupActive === theSavedAutoTopupActive) {
      autoTopupActiveChanged = false;
    }
    let autoTopupAmountChanged = true;
    if (this.state.autoTopupAmount == null || Number(this.state.autoTopupAmount).toFixed(2) === Number(theSavedAutoTopupAmount).toFixed(2)) {
      autoTopupAmountChanged = false;
    }
    let autoTopupThresholdChanged = true;
    if (this.state.autoTopupThreshold == null || Number(this.state.autoTopupThreshold).toFixed(2) === Number(theSavedAutoTopupThreshold).toFixed(2)) {
      autoTopupThresholdChanged = false;
    }

    if (autoTopupActiveChanged === false && autoTopupAmountChanged === false && autoTopupThresholdChanged === false) {
      return true;
    }

    // There has been a change.  Do some validation.
    // - only validate the auto topup amount and threshold if auto topup is active.
    if (autoTopupActiveChanged === true) {
      if (this.state.autoTopupActive === 1) {
        if (!this.isAutoTopupAmountValid(theAccount)) {
          return true;
        }
    
        if (this.state.autoTopupThreshold != null && this.state.autoTopupThreshold < getTopupThresholdMinimum()) {
          return true;
        }
      }
    }
    
    return false;
  }

  // The spend control paper just has the monthly spend limit
  isLimitSaveDisabled(theAccount) {
    const { getMaxMonthlyBudgetLimit, getMaxMonthlyBudgetMinimum } = this.props.auth;

    // Get the saved values to compare against.
    let theSavedMaxMonthlyBudget = theAccount.maxMonthlyBudget;
    if (this.state.savedMaxMonthlyBudgetSet === true) {
      theSavedMaxMonthlyBudget = this.state.savedMaxMonthlyBudget;
    }

    if ( (this.state.maxMonthlyBudgetSet === false || 
          (theSavedMaxMonthlyBudget == null && this.state.maxMonthlyBudget == null) ||
          (parseFloat(theSavedMaxMonthlyBudget) === parseFloat(this.state.maxMonthlyBudget))
         )  ) 
    {
      return true;
    }

    // Do a validation check on the monthly budget amount
    if (this.state.maxMonthlyBudget != null && (this.state.maxMonthlyBudget < getMaxMonthlyBudgetMinimum() || this.state.maxMonthlyBudget > getMaxMonthlyBudgetLimit())) {
      return true;
    }

    return false;
  }

  isSaveAutoPayoutsDisabled(theAccount) {
    //simple saving button
    if (this.state.saveStatusAutoPayout === 'saving')
      return true;

    let disabledByAutoPayout = false;
    if (this.state.savedAutoPayout != null)
      disabledByAutoPayout = disableAttributeCheck(this.state.autoPayout, this.state.savedAutoPayout);
    else
      disabledByAutoPayout = disableAttributeCheck(this.state.autoPayout, theAccount.autoPayout);
    
    let disabledByAutoPayoutRetain = false;
    if (this.state.savedAutoPayoutRetain != null)
      disabledByAutoPayoutRetain = disableAttributeCheck(this.state.autoPayoutRetain, this.state.savedAutoPayoutRetain);
    else
      disabledByAutoPayoutRetain = disableAttributeCheck(this.state.autoPayoutRetain, theAccount.autoPayoutRetain);


    return disabledByAutoPayout && disabledByAutoPayoutRetain;
  }

  toggleAutoTopup(ata, autoTopupAmount, autoTopupThreshold) {
    const { getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit } = this.props.auth;
    if (ata === 1) {
      let theAmount = autoTopupAmount;
      if (theAmount == null || theAmount < getTopupTargetMinimum() || theAmount > getTopupTargetLimit()) {
        theAmount = getTopupTargetDefault();
      }

      let theThreshold = autoTopupThreshold;
      if (theThreshold == null || theThreshold < getTopupThresholdMinimum() || theThreshold > getTopupThresholdLimit()) {
        theThreshold = getTopupThresholdDefault();
      }

      this.setState({ autoTopupActive: 1, autoTopupAmount: theAmount, autoTopupThreshold: theThreshold});
    } else {
      this.setState({ autoTopupActive: 0, autoTopupAmount: 0, autoTopupThreshold: 0});
    }
  }

  toggleAutomaticPayoutAtIntervals(autoPayout) {
    if (autoPayout === 0 || autoPayout == null) {
      this.setState({ autoPayout: 1 });
    } else {
      this.setState({ autoPayout: 0 });
    }
  }

  toggleSetMonthlyBudget(mmb, origBudget) {
    if (mmb === false) {
      // Turn on a budget
      if (origBudget != null) {
        this.setState({maxMonthlyBudget: origBudget, maxMonthlyBudgetSet: true});
      } else {
        // Use a default of 1000
        this.setState({maxMonthlyBudget: 1000.00, maxMonthlyBudgetSet: true});
      }
    } else {
      // Turn off the budget
      this.setState({maxMonthlyBudget: null, maxMonthlyBudgetSet: true});
    }
  }

  handleAddFundsNowClose = () => {
    this.setState({addFundsNowDialog: false});
  };

  render() {
    const classes = this.props.classes;

    const { logError, isUnauthenticatedCustomer, isAuthenticatedCustomer, isAuthenticatedReseller, getMaxMonthlyBudgetLimit, getMaxMonthlyBudgetMinimum, getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit  } = this.props.auth;

    let acctId = this.props.accountId;
    let acctType = this.props.accountType;

    //here we need to figure out if the user is one of us but NOT masquarading.
    // let isUserAppbind = (abEnvironment.getUserAttribute("role") === "appbind_appbind");

    // Limit functionality is used to prevent funding when an account is suspended or pending or cancelled
    let limitFunctionality = true;
    if (this.props.accountStatus === 1 || this.props.accountStatus === 2  || this.props.accountStatus === 3) {
      limitFunctionality = true;
    } else if (this.props.resellerStatus === 1 || this.props.resellerStatus === 2) {
      limitFunctionality = true;
    } else {
      limitFunctionality = false;
    }

    let showLimitRequest = true;
    if (isUnauthenticatedCustomer()) {
      showLimitRequest = false;
    }

    let showPayoutsBlock = true;
    let isCustomer = false;
    if (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) {
      showPayoutsBlock = false;
      isCustomer = true;
    }

    let isReseller = false;
    if (isAuthenticatedReseller()) {
      isReseller = true;
    }

    let showAutoPayout = false;
    let showAutoPayoutFlag = abEnvironment.getFlagValue("showAutoPayout");
    if (showAutoPayoutFlag != null && showAutoPayoutFlag.flagValue === 1) { 
      showAutoPayout = true; 
    }

    //limit the display of the "terminateAccount" segments depending on role and ID (must be customer and must be operating on own account)
    let showTerminateCustomer = (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) && abEnvironment.getUserAttribute("a_id") === acctId;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={FundingSourceBudgetQuery}
        variables={{accountId: acctId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let theAccount = props.accountTByAccountId;

            let autoPayout = theAccount.autoPayout;
            if (autoPayout == null) {
              autoPayout = 0; // temp default
            }
            if (this.state.autoPayout != null) {
              autoPayout = this.state.autoPayout;
            }
            let autoPayoutRetain = theAccount.autoPayoutRetain;
            if (autoPayoutRetain == null) {
              autoPayoutRetain = 0
            }
            if (this.state.autoPayoutRetain != null) {
              autoPayoutRetain = this.state.autoPayoutRetain;
            }

            let autoTopupConfirmMessage = '';
            let autoTopupActive = theAccount.autoTopupActive;
            if (this.state.autoTopupActive != null) {
              autoTopupActive = this.state.autoTopupActive;
            }

            //current auto-topup amounts/states; if saved, use that for payout action, else use returned val from query.
            let currentAutoTopupActive = this.state.savedAutoTopupActive != null ? this.state.savedAutoTopupActive : theAccount.autoTopupActive;
            let currentAutoTopupAmount = 0;
            let currentAutoTopupThreshold = 0;
            if (currentAutoTopupActive === 1) {
              let theAutoTopupAmount = this.state.savedAutoTopupAmount != null ? this.state.savedAutoTopupAmount : theAccount.autoTopupAmount;
              let theAutoTopupThreshold = this.state.savedAutoTopupThreshold != null ? this.state.savedAutoTopupThreshold : theAccount.autoTopupThreshhold;
              if (Number(theAutoTopupAmount) > 0 && Number(theAutoTopupThreshold) > 0) {
                currentAutoTopupAmount = theAutoTopupAmount;
                currentAutoTopupThreshold = theAutoTopupThreshold;
              }
            }

            if (this.state.autoTopupActive === 1) {
              autoTopupConfirmMessage = "When you turn automatic topups on, your funding source will be charged when your available balance goes below the amount you've set here. You'll be billed either the amount you've set to add, or an amount sufficient to bring your balance up to the threshold you've specified.  Are you sure you want to proceed?";
            } else {
              autoTopupConfirmMessage = 'You have chosen to turn off auto topups. Are you sure you want to proceed?';
            }

            /* monthly spend control */
            let useMonthlySpend = false;
            let origMonthlySpend = theAccount.maxMonthlyBudget;
            if (this.state.savedMaxMonthlyBudgetSet === true) {
              origMonthlySpend = this.state.savedMaxMonthlyBudget;
            }
      
            let monthlySpend = origMonthlySpend;

            if (this.state.maxMonthlyBudgetSet === true) {
              monthlySpend = this.state.maxMonthlyBudget;
            }

            if (monthlySpend != null) {
              useMonthlySpend = true;
            }

            let showTopUpHistory = false;

            // Indicate if the budget save button is disabled
            let saveBudgetDisabled = this.isSaveBudgetDisabled(theAccount);

            // Are there active funding sources?
            let activeFundSourceCount = 0;
            theAccount.paymentSourceTsByAccountId.nodes.forEach(n => {
              if ((n.psType === 0 || n.psType === 1) && n.psStatus === 0) {
                activeFundSourceCount++;
              }
            });

            // Indicate if the add funds button is disabled.
            let addFundsDisabled = true;
            if (saveBudgetDisabled === true && limitFunctionality === false && activeFundSourceCount > 0) {
              addFundsDisabled = false;
            }
            if (props.accountTByAccountId.accountStatus === 3) {
              addFundsDisabled = true;
            }

            return (
              <React.Fragment>
                {/* Balance paper segment */}
                <Paper className={classes.paper} id="AppBind_Balance">
                  <Grid container spacing={2} justifyContent="flex-start">
                    <Grid item xs={12}>
                      {isCustomer === true &&
                      <Typography variant="h6" className={classes.sectionTitle}>Your expense wallet</Typography>}
                      {isReseller === true &&
                      <Typography variant="h6" className={classes.sectionTitle}>Your wallet</Typography>}
                      {isCustomer === true &&
                      <Typography variant="body2">
                        Subscriptions and services are paid from your expense wallet balance.  AppBind may also provide you with an overdraft float that allows purchases to exceed your available balance.
                      </Typography>}
                      {isReseller === true &&
                      <Typography variant="body2">
                        Subscriptions and services are paid from your available wallet balance.  AppBind may also provide you with an overdraft float that allows purchases to exceed your available balance.
                      </Typography>}
                    </Grid>
                    <Grid item xs={12}>
                      <BalanceAndFloatWidgets
                        auth={this.props.auth}
                        autoTopupAmount={currentAutoTopupAmount}
                        autoTopupThreshold={currentAutoTopupThreshold}
                        showLimitRequest={showLimitRequest}
                        floatLimit={theAccount.floatLimit}
                        accountId={acctId}
                        accountType={acctType}
                        useBudgets={this.props.useBudgets}
                        narrow={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={0}>   
                        <Grid item xs={12}>
                          <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                            <Grid item>
                              <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                <Grid item>
                                  <Typography variant="body1" style={AppBindStyles.boldLabel}>Automatically add funds</Typography>
                                </Grid>
                                <Grid item>
                                  <Tooltip placement='bottom' title="When your balance goes below the limit you specify, you'll be billed the amount needed to bring your balance back up to your desired funding level.">
                                    <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item>
                              <Switch color="primary"
                                checked={autoTopupActive === 1} 
                                disabled={limitFunctionality === true}
                                onChange={(e, checked) => {
                                  if (checked) {
                                    this.toggleAutoTopup(1, currentAutoTopupAmount, currentAutoTopupThreshold);
                                  } else {
                                    this.toggleAutoTopup(0, currentAutoTopupAmount, currentAutoTopupThreshold);
                                  }                        
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                            <Grid item>
                              <Typography variant="body1" style={autoTopupActive !== 0 ? AppBindStyles.boldLabel : null} >
                                When balance falls below
                              </Typography>
                            </Grid>
                            <Grid item>
                              <CurrencyTextField id="fallsBelowPending" className={classes.currencyField3}
                                value={getValueForRender(this.state.autoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault())}
                                showErrMsg={true}
                                allowNegative={false}
                                minValue={getTopupThresholdMinimum()}
                                maxValue={getTopupThresholdLimit()}
                                minEnterValue={0}
                                maxEnterValue={999999}
                                onChange={(e) => this.setState({ autoTopupThreshold: e.target.value })}
                                disabled={autoTopupActive === 0}
                                textAlign="right"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                            <Grid item>
                              {autoTopupActive === 0 &&
                              <Typography variant="body1">Fund balance to</Typography>}
                              {autoTopupActive !== 0 &&
                              <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>}
                            </Grid>
                            <Grid item>
                              <CurrencyTextField id="autoTopupAmountPending" className={classes.currencyField3}
                                value={getValueForRender(this.state.autoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault())}
                                showErrMsg={true}
                                error={this.isAutoTopupAmountError(theAccount)}
                                errorMsg={this.getAutoTopupAmountErrorMessage(theAccount)}
                                allowNegative={false}
                                minValue={getTopupTargetMinimum()}
                                maxValue={getTopupTargetLimit()}
                                minEnterValue={0}
                                maxEnterValue={99999999}
                                onChange={(e) => this.setState({ autoTopupAmount: e.target.value })}
                                disabled={autoTopupActive === 0}
                                textAlign="right"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                        <Grid item>
                          <Grid container spacing={1} justifyContent="flex-start">
                            <Grid item>
                              <SaveButton
                                snackbarOpen={this.state.saveSnackbarOpenBudget}
                                autoHideDuration={this.state.saveSnackbarDurationBudget}
                                onSaveClick={() => { this._onSaveAutoTopup(theAccount);}}
                                onSnackbarClose={() => { this._onSnackbarClose(); }}
                                status={this.state.saveStatusBudget}
                                message={this.state.saveMessageBudget}
                                disabled={saveBudgetDisabled}
                              />
                            </Grid>
                            <Grid item>
                              <Tooltip title={'Add funds to your account'}>
                                <Button color="primary" 
                                        variant={'outlined'}
                                        disabled={addFundsDisabled === true} 
                                        style={AppBindStyles.bottomButton}
                                        onClick={() => { this.setState({addFundsNowDialog: true});}}>
                                  Add funds now
                                </Button>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        { showTerminateCustomer &&
                        ( this.state.requestCancelSpinner ?
                          <Button color="primary" variant="outlined">
                            <SavingSpinner/>
                          </Button>
                          : props.accountTByAccountId.accountType === 4 && !this.state.requestCancelSpinner &&
                          <Grid item>
                            <Tooltip title={'You can terminate your subscription arrangement and receive a balance refund at any time.'}>
                              <Button color="primary" 
                                variant={'outlined'}
                                disabled={props.accountTByAccountId.accountStatus === 3} 
                                style={AppBindStyles.bottomRightAlignedButton}
                                onClick={() => { this.setState({requestCancelDialog: true});}}
                              >
                                Refund balance
                              </Button>
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Spending paper... */}
                <Paper className={classes.paper} id="AppBind_Controls">
                  <Grid container spacing={2} justifyContent="flex-start">
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your spending</Typography>
                      <Typography variant="body2">
                        AppBind tracks your spending and provides spend controls.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SpendingWidgets accountId={acctId} spendLimit={monthlySpend}/>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                        <Grid item>
                          <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                            <Grid item>
                              <Typography variant="body1" style={AppBindStyles.boldLabel}>Set a monthly spend limit</Typography>
                            </Grid>
                            <Grid item>
                              <Tooltip placement='bottom' title={"If you set a monthly spend limit, we'll decline any subscription charges that would cause you to spend more than this limit in a calendar month."}>
                                <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                            <Grid item>
                              {monthlySpend != null &&
                              <CurrencyTextField id="maxMonthlyBudgetPending" className={classes.currencyField3}
                                value={monthlySpend}
                                showErrMsg={true}
                                allowNegative={false}
                                minValue={getMaxMonthlyBudgetMinimum()}
                                maxValue={getMaxMonthlyBudgetLimit()}
                                minEnterValue={0}
                                maxEnterValue={99999999}
                                onChange={(e) => this.setState({ maxMonthlyBudget: e.target.value, maxMonthlyBudgetSet: true }) }
                                disabled={this.state.autoTopupActive === 0}
                                textAlign="right"
                              />}
                              {monthlySpend == null &&
                              <Typography className={classes.noBudgetHolder} variant="body1"><i>No limit</i></Typography>}
                            </Grid>
                            <Grid item>
                              <Switch checked={useMonthlySpend === true} 
                                      onChange={(e) => this.toggleSetMonthlyBudget(useMonthlySpend, origMonthlySpend)} 
                                      value={useMonthlySpend}
                                      color="primary"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <SaveButton
                        snackbarOpen={this.state.saveSnackbarOpenLimit}
                        autoHideDuration={this.state.saveSnackbarDurationLimit}
                        onSaveClick={() => { this._updateLimits(); }}
                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                        status={this.state.saveStatusLimit}
                        message={this.state.saveMessageLimit}
                        disabled={this.isLimitSaveDisabled(theAccount)}
                      />
                    </Grid>
                  </Grid>
                </Paper>


                {/* Your payouts (autopayout and customize that interval.) */}
                { showPayoutsBlock === true &&
                <Paper className={classes.paper} id="AppBind_Your_Payouts">
                  <Grid container spacing={2} justifyContent="flex-start">
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your payouts</Typography>
                      <Typography variant="body2">
                        View and manage balance payouts
                      </Typography>
                    </Grid>
                    {/* Payout table - list history of payouts */}
                    <Grid item xs={12}>
                      <Payouts accountId={acctId}
                          pAccountId={abEnvironment.getUserAttribute('a_id')}
                          auth={this.props.auth}
                          history={this.props.history}/>
                    </Grid>
                    {/* Payout controls/toggles */}
                    {showAutoPayout &&
                    <React.Fragment>
                    <Grid item xs={12}>
                      <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                        <Grid item>
                          <Typography variant="body1" style={AppBindStyles.boldLabel}>Automatically pay out available balance</Typography>
                          <Typography variant="body2">Payouts happen once a day</Typography>
                        </Grid>
                        <Grid item>
                          <Switch checked={autoPayout === 1} 
                            onChange={(e) => this.toggleAutomaticPayoutAtIntervals(autoPayout)} 
                            value={autoPayout}
                            color="primary"
                            disabled={false}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                        <Grid item>
                          <Typography variant="body1" style={AppBindStyles.boldLabel}>Pay out available balance above this amount</Typography>
                          <Typography variant="body2">Automatic top-up targets higher than this amount will take priority</Typography>
                        </Grid>
                        <Grid item>
                          <CurrencyTextField id="autoPayoutAmount" className={classes.currencyField3}
                            value={getValueForRender(this.state.autoPayoutRetain, theAccount.autoPayoutRetain, 0)}
                            showErrMsg={true}
                            allowNegative={false}
                            minValue={0}
                            maxValue={9999}
                            minEnterValue={0}
                            maxEnterValue={99999999}
                            onChange={(e) => this.setState({ autoPayoutRetain: (e.target.value) ? e.target.value : '0' })}
                            textAlign="right"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <SaveButton
                        snackbarOpen={this.state.saveSnackbarOpenAutoPayout}
                        autoHideDuration={this.state.saveSnackbarDurationAutoPayout}
                        onSaveClick={() => { this._updateAutoPayoutConditions(); }}
                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                        status={this.state.saveStatusAutoPayout}
                        message={this.state.saveMessageAutoPayout}
                        disabled={this.isSaveAutoPayoutsDisabled(theAccount)}
                      />
                    </Grid>
                    </React.Fragment>
                    }
                  </Grid>
                </Paper>}

                <PaymentSourceDialog
                  title="Add funds now"
                  open={this.state.addFundsNowDialog}
                  onClose={this.handleAddFundsNowClose}
                >
                  <AddFundsForm accountId={acctId} auth={this.props.auth} onClose={this.handleAddFundsNowClose} />
                </PaymentSourceDialog>
                
                <ConfirmationDialog
                    open={this.state.confirmAutoTopup}
                    onClose={() => { this.setState({confirmAutoTopup:false});}}
                    onConfirm={() => {  this._updateAccountAutoTopup(theAccount); }}
                    onCancel={() => { }}
                    title="Auto topup"
                    cancelBtn="Cancel"
                    okBtn="Ok"
                    irreversibleAction={true}
                  >
                    {autoTopupConfirmMessage}
                </ConfirmationDialog>  

                {showTerminateCustomer && props.accountTByAccountId.accountType === 4 &&
                  <RefundAndTerminateDialog account={theAccount} auth={this.props.auth}
                    setSaveSpinner={ (value) => { this.setState( { requestCancelSpinner: value }) }}
                    open={this.state.requestCancelDialog}
                    onClose={() => { this.setState({ requestCancelDialog: false }); }}
                    onCancel={() => { this.setState({ requestCancelDialog: false }); }}
                  />
                }

                {showTopUpHistory === true && 
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" >
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Grid container spacing={0}>
                              <Grid item >
                                <Typography align="left" variant="h6">Top-up history</Typography>
                              </Grid>                          
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <TopUps auth={this.props.auth} accountId={acctId}/>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                }

                {this.props.auth.isUnauthenticatedCustomer() && false &&
                <Grid  container direction="row" justifyContent="center" alignItems="center">
                  <PoweredBy/>
                </Grid>
                }
              </React.Fragment>
            );
          }
          return <div><LoadingSpinner/></div>
        }}
      />
    );
  }
  
  _onSnackbarClose() {
    this.setState( {saveSnackbarOpenLimit: false, saveStatusLimit: '', saveMessageLimit: '', saveSnackbarDurationLimit: null,
                    saveSnackbarOpenBudget: false, saveStatusBudget: '', saveMessageBudget: '', saveSnackbarDurationBudget: null,
                    saveSnackbarOpenAutoPayout: false, saveStatusAutoPayout: '', saveMessageAutoPayout: '', saveSnackbarDurationAutoPayout: null,
                    saveSnackbarOpenAddNow: false, saveStatusAddNow: '', saveMessageAddNow: '', saveSnackbarDurationAddNow: null });
  }

  _onSaveAutoTopup(theAccount) {
    let theSavedAutoTopupActive = theAccount.autoTopupActive;
    if (this.state.savedAutoTopupActive != null) {
      theSavedAutoTopupActive = this.state.savedAutoTopupActive;
    }

    if (this.state.autoTopupActive != null && theSavedAutoTopupActive !== this.state.autoTopupActive) {
      this.setState({confirmAutoTopup:true});
    } else {
      this._updateAccountAutoTopup(theAccount);
    }
  }

  /**
   * call to backend to update the recurring payout conditions and limits.
   */
  _updateAutoPayoutConditions() {
    let accountId = this.props.accountId

    this.setState({ saveStatusAutoPayout: 'saving' });
    const { autoPayout, autoPayoutRetain } = this.state;

    AutomaticPayoutFieldsAccountUpdateMutation.commit(accountId, autoPayout, autoPayoutRetain)
      .then( response => {
        if (response.updateAccountTByAccountId == null) {
          // there's a problem...
          this.setState({ saveSnackbarOpenAutoPayout: true, saveStatusAutoPayout: 'error',
            saveMessageAutoPayout: 'Problem saving automated payout settings', saveSnackbarDurationAutoPayout: null
          });
          return;
        }

        //no problem, correct update
        // console.log('Account updated')
        this.setState({ saveSnackbarOpenAutoPayout: true, saveStatusAutoPayout: 'success',
          saveMessageAutoPayout: 'Automated payout options saved', saveSnackbarDurationAutoPayout: 3000,
          savedAutoPayoutRetain: autoPayoutRetain,
          savedAutoPayout: autoPayout
        });
      })
      .catch(error => {
        // console.log('Got error: ' + error);
        this.setState({ saveSnackbarOpenAutoPayout: true, saveStatusAutoPayout: 'error',
          saveMessageAutoPayout: 'Problem saving automated payout settings', saveSnackbarDurationAutoPayout: null
        });
      });
  }


  /* Call a backend method to update and see if a charge should happen right away */
  _updateAccountAutoTopup(theAccount) {

    this.setState({ saveStatusBudget: 'saving' });
    const { autoTopupActive, autoTopupAmount, autoTopupThreshold, savedAutoTopupActive } = this.state;

    const { callPartnerApi } = this.props.auth;

    let usingAccountId = this.props.accountId; 
    let pAccountId = abEnvironment.getUserAttribute('a_id');

    /*
     * Make sure that there is something to do
     */
    if (autoTopupActive == null && autoTopupAmount == null && autoTopupThreshold == null) {
      let theState = {
        saveSnackbarOpenBudget: true, 
        saveStatusBudget: 'success', 
        saveMessageBudget: 'There was not any budget information to save.', 
        saveSnackbarDurationBudget: 3000
      };
      this.setState(theState);
      return;
    }

    // What is the current state of autoTopupActive
    let currentAutoTopupActive = theAccount.autoTopupActive;
    if (savedAutoTopupActive != null) {
      currentAutoTopupActive = savedAutoTopupActive;
    }

    /*
     * As of September 2021, we're also passing payFromBalance, and if that is all that is passed in (other
     * parms are null), the API will just update payFromBalance.
     */
    let payload = {
      operation: "saveAndProcessAutoTopup",
      params: {
        accountId:                usingAccountId,
        provisioningAccountId:    pAccountId,
        payFromBalance:           null, // Not changing in this page
        autoTopupActive:          autoTopupActive,
        currentAutoTopupActive:   currentAutoTopupActive,
        autoTopupAmount:          autoTopupAmount,
        autoTopupThreshold:       autoTopupThreshold
      }
    }
    
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    ).then(response => {
      return response.json();
    })
    .then(data => {
      if (data.status === 0 || data.status === 1) {
        let theSaveMessage = 'Budget changes were saved';
        if (data.status === 1) {
          theSaveMessage = 'Budget changes were saved and processed';
        }

        let theState = {
          saveSnackbarOpenBudget: true, 
          saveStatusBudget: 'success', 
          saveMessageBudget: theSaveMessage, 
          saveSnackbarDurationBudget: 3000
        };

        if (autoTopupActive != null) {
          theState.savedAutoTopupActive = autoTopupActive;
        }
        if (autoTopupAmount != null) {
          theState.savedAutoTopupAmount = autoTopupAmount;
        }
        if (autoTopupThreshold != null) {
          theState.savedAutoTopupThreshold = autoTopupThreshold;
        }
  
        this.setState(theState);
      } else {
        throw data;
      }
    })
    .catch(error => {
      //console.log('Got error: ' + JSON.stringify(error));
      // this is sort of a hack as a -7 usually means no funding source but it could mean other things.
      if (error.status && error.status === -7) {

        let theState = {
          saveSnackbarOpenBudget: true, 
          saveStatusBudget: 'success', 
          saveMessageBudget: 'Auto topup saved but funding source was not charged.', 
          saveSnackbarDurationBudget: 6000
        };

        if (autoTopupActive != null) {
          theState.autoTopupActive = autoTopupActive;
        }
        if (autoTopupAmount != null) {
          theState.autoTopupAmount = autoTopupAmount;
        }
        if (autoTopupActive != null) {
          theState.autoTopupThreshold = autoTopupThreshold;
        }
  
        this.setState(theState);
      } else {
        this.setState({ saveSnackbarOpenBudget: true, saveStatusBudget: 'error', saveMessageBudget: 'Unable to save budget changes.', saveSnackbarDurationBudget: null  });
      }
    });
  }

  _updateLimits() {
    let accountId = this.props.accountId

    this.setState({ saveStatusLimit: 'saving' });
    const { maxMonthlyBudget, maxMonthlyBudgetSet } = this.state;

    // We shouldn't be able to update if maxMonthlyBudgetSet isn't set, but just to be sure, check it 
    if (maxMonthlyBudgetSet !== true) {
      return;
    }

    FundingSourceBudgetAccountUpdateMutation.commit(accountId, maxMonthlyBudget)
    .then(response => {
      // console.log('Account updated')
      this.setState({ saveSnackbarOpenLimit: true, saveStatusLimit: 'success', saveMessageLimit: 'Monthly spend limit saved', saveSnackbarDurationLimit: 3000,
                      savedMaxMonthlyBudget: maxMonthlyBudget,  savedMaxMonthlyBudgetSet: true });
      })
    .catch(error => {
      // console.log('Got error: ' + error);
      this.setState({ saveSnackbarOpenLimit: true, saveStatusLimit: 'error', saveMessageLimit: 'Problem saving account limits', saveSnackbarDurationLimit: null  });
    });
  }
}
  
FundingSourceBudget.propTypes = {
    classes: PropTypes.object.isRequired,
  };
      
export default withStyles(styleSheet)(FundingSourceBudget);
